import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon } from '@heroicons/react/24/outline';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Input, CustomSelect, Button, ListSearchInput, ListPagination } from '@redpanda/ui';
import { useListSearch, useRememberedState, emitDataChanged } from '@redpanda/utils';
import toast from 'react-hot-toast';
import { useCustomers } from '../../stores/useCustomerStore';
import { customerApi } from '../../services/api';
import CustomerDetailDialog from '../../components/CustomerDetailDialog';
import { TableSkeleton } from '@redpanda/ui';

const CustomersPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const {
    customers,
    pagination,
    isLoading,
    fetchCustomers,
    setCurrentPage,
    refreshData,
  } = useCustomers();

  // 顧客詳情對話框不記住（原本重新整理會自己跳出來）
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // 搜尋、篩選由後端查全部顧客（原本只過濾目前這一頁，第 2 頁以後搜不到）
  // VIP 等級記住上次的選擇；搜尋文字、次數、金額（打字輸入）不記住
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVipLevel, setSelectedVipLevel] = useRememberedState('booking-ui:customers.vipLevel', '', ['', 'REGULAR', 'VIP', 'SVIP']);
  const [minVisitCount, setMinVisitCount] = useState('');
  const [minSpent, setMinSpent] = useState('');
  const search = searchTerm.trim();
  const hasFilters = !!(search || selectedVipLevel || minVisitCount || minSpent);
  const filterQuery = {
    search: search || undefined,
    vipLevel: selectedVipLevel || undefined,
    minVisitCount: minVisitCount || undefined,
    minSpent: minSpent || undefined,
  };
  const searchResult = useListSearch<any>(
    'customers',
    hasFilters ? filterQuery : null,
    page => customerApi.getPaginated(page, 20, filterQuery),
    () => toast.error(t('translation:common.loadFailed', '載入失敗')),
    1,
    { textFields: ['search', 'minVisitCount', 'minSpent'] }
  );
  const filteredCustomers = searchResult.active ? searchResult.items : (Array.isArray(customers) ? customers : []);
  const displayPagination = searchResult.active ? searchResult.pagination : pagination;

  // 清除篩選
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedVipLevel('');
    setMinVisitCount('');
    setMinSpent('');
  };

  // 初次加載
  useEffect(() => {
    fetchCustomers(1);
  }, [fetchCustomers]);

  const handleViewDetail = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setShowDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setShowDetailDialog(false);
    setSelectedCustomerId(null);
  };

  const handleUpdateCustomer = () => {
    refreshData();
    emitDataChanged('customers'); // 搜尋結果重抓
  };

  const handlePageChange = (page: number) => {
    if (searchResult.active) {
      searchResult.setPage(page);
      return;
    }
    setCurrentPage(page);
    fetchCustomers(page);
  };

  const getVipLevelBadge = (vipLevel?: string) => {
    const levelMap: Record<string, { className: string }> = {
      VIP: { className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' },
      SVIP: { className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' },
      REGULAR: { className: 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300' },
    };

    const level = levelMap[vipLevel || 'REGULAR'];
    const label = t(`translation:vipLevel.${vipLevel || 'REGULAR'}` as any);

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${level.className}`}>
        {label}
      </span>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      {/* 標題區 */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t('pages:admin.customers.title')}
        </h1>
      </div>

      {/* 搜尋和篩選區域 */}
      <div className="mb-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* 搜尋框 */}
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('pages:admin.customers.searchPlaceholder')} widthClass="" />

          {/* VIP 等級篩選 */}
          <div>
            <CustomSelect
              value={selectedVipLevel}
              onChange={setSelectedVipLevel}
              placeholder={t('pages:admin.customers.selectVipLevel')}
              className="h-10"
              options={[
                { value: '', label: t('pages:admin.customers.allLevels') },
                { value: 'REGULAR', label: t('translation:vipLevel.REGULAR') },
                { value: 'VIP', label: t('translation:vipLevel.VIP') },
                { value: 'SVIP', label: t('translation:vipLevel.SVIP') }
              ]}
            />
          </div>

          {/* 最低到訪次數 */}
          <div>
            <Input
              type="number"
              value={minVisitCount}
              onChange={(e) => setMinVisitCount(e.target.value)}
              placeholder={t('pages:admin.customers.minVisitCount')}
              min="0"
              className="w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          {/* 最低消費金額 */}
          <div>
            <Input
              type="number"
              value={minSpent}
              onChange={(e) => setMinSpent(e.target.value)}
              placeholder={t('pages:admin.customers.minSpent')}
              min="0"
              className="w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          {/* 清除篩選按鈕 */}
          <div className="flex flex-col space-y-2">
            <Button
              type="button"
              onClick={clearFilters}
              variant="ghost"
              className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-md h-10 flex items-center justify-center"
            >
              {t('translation:common.clearFilters')}
            </Button>
          </div>
        </div>
      </div>

      {/* 手機版卡片列表 */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t('translation:common.loading')}
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {(searchTerm || selectedVipLevel || minVisitCount || minSpent)
              ? t('translation:common.noMatchingCustomers')
              : t('translation:common.noCustomers')
            }
          </div>
        ) : (
          filteredCustomers.map((customer) => (
            <div
              key={customer.id}
              className="bg-white dark:bg-slate-800 shadow rounded-lg p-3 space-y-3"
            >
              {/* 客戶基本資訊 */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                    {customer.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {customer.phone}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {getVipLevelBadge(customer.vipLevel)}
                </div>
              </div>

              {/* 統計資料 */}
              <div className="grid grid-cols-3 gap-3 text-center border-t border-gray-200 dark:border-slate-700 pt-3">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('translation:common.totalSpent')}</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    ${Math.floor(Number(customer.totalSpent))}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('translation:common.visitCount')}</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {customer.visitCount} {t('translation:common.times')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('translation:common.lastVisit')}</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {formatDate(customer.lastVisit)}
                  </p>
                </div>
              </div>

              {/* 操作按鈕 */}
              <button
                onClick={() => handleViewDetail(customer.id)}
                className="w-full min-h-[44px] flex items-center justify-center bg-brand-600 hover:bg-brand-700 text-white font-medium py-2 px-4 rounded transition-colors border-t border-gray-200 dark:border-slate-700 pt-3"
                title={t('translation:common.viewDetails')}
              >
                <EyeIcon className="h-5 w-5" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={displayPagination} onPageChange={handlePageChange} className="mb-3" />

      {/* 桌面版表格列表 */}
      <div className="hidden md:block">
        {isLoading ? (
          <TableSkeleton rows={10} columns={7} />
        ) : (
          <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
            <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[15%]">{t('translation:common.name')}</TableHead>
                    <TableHead className="w-[15%]">{t('translation:common.phone')}</TableHead>
                    <TableHead className="w-[10%]">{t('translation:common.vipLevel')}</TableHead>
                    <TableHead className="w-[12%]">{t('translation:common.totalSpent')}</TableHead>
                    <TableHead className="w-[10%]">{t('translation:common.visitCount')}</TableHead>
                    <TableHead className="w-[12%]">{t('translation:common.lastVisit')}</TableHead>
                    <TableHead className="w-[8%]">{t('translation:common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {(searchTerm || selectedVipLevel || minVisitCount || minSpent)
                          ? t('translation:common.noMatchingCustomers')
                          : t('translation:common.noCustomers')
                        }
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell className="truncate font-medium" title={customer.name}>
                          {customer.name}
                        </TableCell>
                        <TableCell>{customer.phone}</TableCell>
                        <TableCell>{getVipLevelBadge(customer.vipLevel)}</TableCell>
                        <TableCell>${Math.floor(Number(customer.totalSpent))}</TableCell>
                        <TableCell>{customer.visitCount}</TableCell>
                        <TableCell>{formatDate(customer.lastVisit)}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleViewDetail(customer.id)}
                            className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
                            title={t('translation:common.viewDetails')}
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* 表格下方的分頁控制 */}
      <ListPagination pagination={displayPagination} onPageChange={handlePageChange} className="mt-6" />

      {/* 客戶詳情對話框 */}
      <CustomerDetailDialog
        isOpen={showDetailDialog}
        onClose={handleCloseDetailDialog}
        customerId={selectedCustomerId}
        onUpdate={handleUpdateCustomer}
      />
    </div>
  );
};

export default CustomersPage;
